import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  X,
  FileText,
  Calendar,
  Tag,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import type { EstimateViewItem } from "@shared-core/data/api-types";
import {
  contributionPercent,
  estimateStatusLabel,
  estimateStatusTone,
} from "@shared-core/domain/estimate-presentation";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import {
  DRAFT_ESTIMATE_STATE_LABELS,
  draftEstimateStateTone,
} from "@shared-core/domain/merchandise-presentation";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";

interface EstimateDetailModalProps {
  estimate: EstimateViewItem;
  onClose: () => void;
}

export function EstimateDetailModal({
  estimate,
  onClose,
}: EstimateDetailModalProps) {
  // Same fallback EstimateCard uses: the Tharstern number once it has
  // synced, the internal id before that. The detail route accepts both and
  // canonicalises to the number.
  const detailId = estimate.estimate_id || estimate.id;
  const marginPct = contributionPercent(
    estimate.estimate_amt,
    estimate.contribution_amt,
  );
  // As on EstimateCard: a merchandise draft wears its own state, and one
  // still awaiting the team's quote has no amount rather than a £0.00.
  const draftState = estimate.draft_state ?? null;
  const awaitingQuote = draftState === "awaiting_quote";

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 bg-background border-b border-border px-6 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <FileText className="size-6 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg mb-1">Estimate Details</h2>
              <div className="flex items-center gap-2 flex-wrap">
                {draftState ? (
                  <Badge variant={draftEstimateStateTone(draftState)}>
                    {DRAFT_ESTIMATE_STATE_LABELS[draftState]}
                  </Badge>
                ) : (
                  <Badge variant={estimateStatusTone(estimate.status)}>
                    {estimateStatusLabel(estimate.status)}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">
                  {estimate.estimate_id}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* The way out of the quick-look and into the estimate itself,
                where Record outcome and Park / chase live. Without it the list
                dead-ends here: clicking an estimate opens this modal, the
                modal linked only to the customer, and EstimateDetailPage was
                reachable by typing its URL and no other way. Three reps asked
                for a "park" control that had already shipped, because nothing
                in the product led to the page holding it. */}
            <Button variant="outline" size="sm" asChild>
              <Link
                to={paths.estimateDetail({ id: detailId })}
                onClick={onClose}
              >
                Open full estimate
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
          {/* Description */}
          <div>
            <label className="text-sm text-muted-foreground block mb-1">
              Description
            </label>
            <p className="text-sm">{estimate.description}</p>
          </div>

          {/* Customer */}
          {estimate.customer && (
            <div>
              <label className="text-sm text-muted-foreground block mb-1">
                Customer
              </label>
              <Link
                to={paths.customerDetail({ customerId: estimate.customer.id })}
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                {estimate.customer.name}
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          )}

          {/* Financial Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-1.5">
              <TrendingUp className="size-4" />
              Financial Summary
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">
                  Estimate Amount
                </label>
                <p className="text-xl font-semibold">
                  {awaitingQuote
                    ? "Awaiting quote"
                    : `£${estimate.estimate_amt.toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                </p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">
                  Contribution
                </label>
                <p className="text-xl font-semibold">
                  {awaitingQuote
                    ? "—"
                    : `£${estimate.contribution_amt.toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {awaitingQuote || marginPct == null
                    ? "— margin"
                    : `${marginPct.toFixed(1)}% margin`}
                </p>
              </div>
            </div>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-1 flex items-center gap-1">
                <Calendar className="size-3.5" />
                Estimate Date
              </label>
              <p className="text-sm">
                {formatDateOrDash(estimate.estimate_date)}
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1 flex items-center gap-1">
                <Tag className="size-3.5" />
                Status
              </label>
              <p className="text-sm capitalize">{estimate.status}</p>
            </div>
          </div>

          {/* Source System */}
          <div>
            <label className="text-sm text-muted-foreground block mb-1">
              Tharstern Estimate Number
            </label>
            <code className="font-mono text-sm bg-muted px-2 py-1 rounded inline-block">
              {estimate.estimate_id}
            </code>
          </div>

          {/* IDs */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="text-sm font-medium">Technical Details</h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="text-muted-foreground block mb-0.5">
                  Estimate ID
                </label>
                <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                  {estimate.id}
                </code>
              </div>
              <div>
                <label className="text-muted-foreground block mb-0.5">
                  Customer ID
                </label>
                <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                  {estimate.customer_id}
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-background border-t border-border px-6 py-4 flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
